import { createApi } from "@reduxjs/toolkit/query/react";
import { MODIFIER_GROUP, TagId, TagType } from "../../configs";
import {
  HttpMethod,
  IWriteModifierGroup,
  ModifierGroup,
  ModifierGroupDetails,
  PaginatedResponse,
  SearchByDisplayName,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

const modifierGroupApi = createApi({
  reducerPath: MODIFIER_GROUP.reducerPath,
  baseQuery: createBaseQuery(MODIFIER_GROUP.baseUrl),
  tagTypes: MODIFIER_GROUP.tagTypes,
  endpoints(builder) {
    return {
      fetchModifierGroups: builder.query<
        PaginatedResponse<ModifierGroup>,
        { restaurantId: string; searchQuery: SearchByDisplayName }
      >({
        query: ({ restaurantId, searchQuery }) => {
          return {
            url: `/${restaurantId}/restaurant`,
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.ModifierGroup, id: TagId.LIST }],
      }),

      fetchAllModifierGroups: builder.query<ModifierGroup[], string>({
        query: (restaurantId) => {
          return {
            url: `/${restaurantId}/restaurant/all`,
            method: HttpMethod.GET,
          };
        },
        providesTags: () => [
          { type: TagType.ModifierGroup, id: TagId.ALL_LIST },
        ],
      }),

      createModifierGroup: builder.mutation<
        ModifierGroupDetails,
        IWriteModifierGroup
      >({
        query: (body) => ({
          url: "",
          method: HttpMethod.POST,
          body,
        }),
        invalidatesTags: () => [
          { type: TagType.ModifierGroup, id: TagId.ALL_LIST },
          { type: TagType.ModifierGroup, id: TagId.LIST },
        ],
      }),

      updateModifierGroup: builder.mutation<
        void,
        { modifierGroupId: string; body: IWriteModifierGroup }
      >({
        query: ({ modifierGroupId, body }) => ({
          url: `/${modifierGroupId}`,
          method: HttpMethod.PUT,
          body,
        }),
        invalidatesTags: (_result, _error, { modifierGroupId }) => [
          { type: TagType.ModifierGroup, id: TagId.ALL_LIST },
          { type: TagType.ModifierGroup, id: TagId.LIST },
          { type: TagType.ModifierGroup, id: modifierGroupId },
        ],
      }),

      fetchModifierGroupDetails: builder.query<ModifierGroupDetails, string>({
        query: (modifierGroupId) => {
          return {
            url: `/${modifierGroupId}`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, modifierGroupId) => [
          { type: TagType.ModifierGroup, id: modifierGroupId },
        ],
      }),

      deleteModifierGroup: builder.mutation<void, string>({
        query: (modifierGroupId) => ({
          url: `/${modifierGroupId}`,
          method: HttpMethod.DELETE,
        }),
        invalidatesTags: (_result, _error, modifierGroupId) => [
          { type: TagType.ModifierGroup, id: TagId.ALL_LIST },
          { type: TagType.ModifierGroup, id: TagId.LIST },
          { type: TagType.ModifierGroup, id: modifierGroupId },
        ],
      }),
    };
  },
});

export const {
  useFetchModifierGroupsQuery,
  useFetchModifierGroupDetailsQuery,
  useCreateModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useDeleteModifierGroupMutation,
  useFetchAllModifierGroupsQuery,
} = modifierGroupApi;

export { modifierGroupApi };
